import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const QRScanPage = () => {
  const { selectedProfile } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const scannerRef = useRef(null);
  const mountedRef = useRef(true);

  const handleScan = useCallback(async (token) => {
    setSubmitting(true);
    setScanning(false);
    setError('');
    try {
      const { data } = await api.post('/attendance/qr/scan', {
        token,
        studentId: selectedProfile?._id
      });
      setResult(data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to mark attendance';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [selectedProfile?._id]);

  const initScanner = useCallback(async () => {
    try {
      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          await scanner.stop().catch(() => {});
          scannerRef.current = null;
          if (mountedRef.current) handleScan(decodedText);
        },
        () => {}
      );
    } catch (err) {
      if (mountedRef.current) {
        setScanning(false);
        setError('Could not access camera. Please allow camera permissions and try again.');
      }
    }
  }, [handleScan]);

  useEffect(() => {
    if (scanning) {
      const timer = setTimeout(initScanner, 100);
      return () => clearTimeout(timer);
    }
  }, [scanning, initScanner]);

  const startScanner = () => {
    setResult(null);
    setError('');
    setScanning(true);
  };

  const resetScan = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
    setResult(null);
    setError('');
    setScanning(true);
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, []);

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Scan QR Code</h1>
      {selectedProfile && !selectedProfile.isSelf && (
        <p className="text-sm text-gray-500 mb-6">Scanning as <span className="font-medium text-brand-600">{selectedProfile.name}</span>. Switch profile in the header to scan for someone else.</p>
      )}
      {(!selectedProfile || selectedProfile.isSelf) && <div className="mb-6" />}

      <div className="max-w-md mx-auto">
        {/* Success state */}
        {result && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Attendance Marked!</h3>
            {result.courses?.map((c, i) => (
              <p key={i} className={`text-sm ${c.alreadyMarked ? 'text-gray-400' : 'text-gray-700'}`}>
                {c.courseName}{c.alreadyMarked ? ' (already marked)' : ''}
              </p>
            ))}
            <p className="text-sm text-gray-400 mt-2">
              {new Date(result.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })}
            </p>
            <button
              onClick={resetScan}
              className="mt-4 bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
            >
              Scan Again
            </button>
          </div>
        )}

        {/* Error state */}
        {error && !scanning && !result && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Scan Failed</h3>
            <p className="text-sm text-red-600 mb-4">{error}</p>
            <button
              onClick={resetScan}
              className="bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Scanner */}
        {!result && !error && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            {scanning ? (
              <div>
                <div id="qr-reader" className="w-full" />
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-500">Point your camera at the QR code shown by your teacher</p>
                </div>
              </div>
            ) : submitting ? (
              <div className="p-12 text-center">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-500">Marking attendance...</p>
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-brand-50 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75H16.5v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75H16.5v-.75z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Ready to Scan</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Scan the QR code displayed by your teacher to mark your attendance
                </p>
                <button
                  onClick={startScanner}
                  className="bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
                >
                  Open Camera
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QRScanPage;
